import vkBridge from '@vkontakte/vk-bridge';

export interface VKFriend {
  id: number;
  first_name: string;
  last_name: string;
  photo_100?: string;
  sex?: number; // 1 female, 2 male, 0 unknown
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('VK bridge timeout')), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

function normalizeFriends(users: Array<{
  id: number;
  first_name: string;
  last_name?: string;
  photo_100?: string;
  photo_200?: string;
  sex?: number;
}>): VKFriend[] {
  return users.map((u) => ({
    id: u.id,
    first_name: u.first_name,
    last_name: u.last_name || '',
    photo_100: u.photo_100 || u.photo_200,
    sex: u.sex,
  }));
}

/**
 * Primary: native Mini App picker VKWebAppGetFriends (no access_token).
 * Fallback: user token + friends.get via VKWebAppCallAPIMethod (no raw fetch).
 */
export async function getVKFriends(appId: number, count = 200): Promise<VKFriend[]> {
  // 1) Native picker / friends bridge
  try {
    const res = await withTimeout(
      vkBridge.send('VKWebAppGetFriends', { multi: true }),
      30000,
    ) as { users?: Array<{ id: number; first_name: string; last_name?: string; photo_200?: string; sex?: number }> };
    const users = res?.users || [];
    if (users.length > 0) {
      return normalizeFriends(users);
    }
    // empty list is valid — user has no friends to import
    return [];
  } catch (e) {
    // fall through to API method
    console.warn('VKWebAppGetFriends failed, trying CallAPIMethod', e);
  }

  // 2) Token + friends.get via bridge
  let token: string;
  try {
    const auth = await withTimeout(
      vkBridge.send('VKWebAppGetAuthToken', {
        app_id: appId,
        scope: 'friends',
      }),
      30000,
    ) as { access_token?: string };
    if (!auth?.access_token) {
      throw new Error('Нет доступа к друзьям. Разрешите доступ в VK');
    }
    token = auth.access_token;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/timeout/i.test(msg)) {
      throw new Error('VK не ответил. Попробуйте ещё раз');
    }
    if (/deny|denied|cancel|refuse|access/i.test(msg)) {
      throw new Error('Нет доступа к друзьям. Разрешите доступ в VK');
    }
    throw new Error('Функция работает внутри VK');
  }

  try {
    const res = await withTimeout(
      vkBridge.send('VKWebAppCallAPIMethod', {
        method: 'friends.get',
        params: {
          access_token: token,
          v: '5.199',
          order: 'name',
          count: String(count),
          fields: 'photo_100,sex',
        },
      }),
      15000,
    ) as { response?: { items?: Array<{ id: number; first_name: string; last_name?: string; photo_100?: string; sex?: number }> } };

    const items = res?.response?.items || [];
    return normalizeFriends(items);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/timeout/i.test(msg)) {
      throw new Error('VK не ответил. Попробуйте ещё раз');
    }
    if (/error|fail/i.test(msg)) {
      throw new Error('Не удалось получить список друзей VK');
    }
    throw new Error(msg || 'Не удалось получить список друзей VK');
  }
}

/** @deprecated use getVKFriends — kept for compatibility */
export async function getVKFriendsToken(appId: number): Promise<string> {
  const res = await withTimeout(
    vkBridge.send('VKWebAppGetAuthToken', {
      app_id: appId,
      scope: 'friends',
    }),
    30000,
  ) as { access_token?: string };
  if (!res?.access_token) {
    throw new Error('VK token not granted');
  }
  return res.access_token;
}
